import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { RouteConstants } from "../../../../constants/ApplicationConstants/RouteConstants";
import { CustomNotification } from "../../../../util/CustomNotification";
import * as webUserManagementService from "../../../../services/webUserManagementService";
import * as authService from "../../../../services/authService";
import useGetWebUserList from "../../../../hooks/useGetWebUserList";
import useValue from "../../../../hooks/useValue";

import type { GetAllUserResponse } from "../../../../shared/interface/UserManagement/user.interface";

export const useWebUserList = (props: { searchedText: string | undefined }) => {
  /* CONSTANTS */
  const navigate = useNavigate();

  /* STATE */
  const [userId, setUserId] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);

  /* CUSTOM HOOKS */
  const { loading, userList, handleRefetchUserList, pagination, handleChangePagination, filteredTableData, handleFilterTableData } = useGetWebUserList();
  const { value: showDeleteModal, handleEnableValue: handleShowDeleteModal, handleDisableValue: handleHideDeleteModal } = useValue(false);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);
  const { value: showResetModal, handleEnableValue: handleShowResetPwdModal, handleDisableValue: handleHideResetPwdModal } = useValue(false);
  const { value: savingResetBtn, handleEnableValue: handleEnableSavingResetBtn, handleDisableValue: handleDisableSavingResetBtn } = useValue(false);

  /* HANDLERS */
  const handleAddUser = () => {
    navigate(`${RouteConstants.USER_MANAGEMENT_PAGE}/${RouteConstants.USER_FORM_ADD_PAGE}`);
  };

  const handleEditUser = (record: GetAllUserResponse) => {
    navigate(`${RouteConstants.USER_MANAGEMENT_PAGE}/${RouteConstants.USER_FORM_UPDATE_PAGE}/${record?.id}`);
  };

  const handleViewUser = (record: GetAllUserResponse) => {
    navigate(`${RouteConstants.USER_MANAGEMENT_PAGE}/${RouteConstants.USER_FORM_VIEW_PAGE}/${record?.id}`);
  };

  const handleDeleteUser = (record: GetAllUserResponse) => {
    setUserId(record?.id);
    setIsActive(record?.is_active);
    setUserName(record?.username);
    handleShowDeleteModal();
  };

  const handleOk = () => {
    deleteUserById(userId, isActive);
  };

  const resetState = () => {
    setUserId(null);
    setIsActive(false);
    setUserName(null);
    handleHideDeleteModal();
  };

  const deleteUserById = async (userId: number | null, isActive: boolean) => {
    try {
      if (!userId) return;
      handleEnableSaving();

      await webUserManagementService.deleteUser(userId);
      CustomNotification.showSuccessMessage(`User ${isActive ? "Inactivated" : "Activated"} Successfully.`);
      handleRefetchUserList();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
      resetState();
    }
  };

  const handleCancel = () => {
    resetState();
  };

  /* Reset Password By Admin  */
  const handleResetPassword = (record: GetAllUserResponse) => {
    setUserId(record?.id);
    handleShowResetPwdModal();
  };

  const handleSubmitResetPassword = async (data: { newPassword: string; confirmPassword: string }) => {
    try {
      if (!userId) return;
      handleEnableSavingResetBtn();
      const response = await authService.resetPasswordByAdmin(data.newPassword, userId);
      CustomNotification.showSuccessMessage(response.message);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setUserId(null);
      handleHideResetPwdModal();
      handleDisableSavingResetBtn();
    }
  };

  const handleCancelResetPassword = () => {
    handleHideResetPwdModal();
    setUserId(null);
  };

  const ResetPasswordProps = {
    showResetModal,
    handleSubmitResetPassword,
    handleCancelResetPassword,
    saving: savingResetBtn
  };

  /* EFFECTS */
  useEffect(() => {
    handleFilterTableData(props.searchedText);

    // eslint-disable-next-line
  }, [props.searchedText]);

  return { loading, userList, filteredTableData, pagination, handleAddUser, handleChangePagination, handleViewUser, handleEditUser, handleDeleteUser, showDeleteModal, handleCancel, handleOk, userName, saving, isActive, userId, handleResetPassword, ResetPasswordProps };
};
